import { useContext, useEffect, useMemo } from "react";
import { useAuthQuery as useQuery } from './useAuthQuery';
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { BaseContext } from "../base-context";

function stableDeviceId() {
  const k = "presenceDeviceId";
  let id = localStorage.getItem(k);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(k, id);
  }
  return id;
}

export function usePresence(page?: string) {
  const deviceId = useMemo(stableDeviceId, []);
  const heartbeat = useMutation(api.presence.heartbeat);
  const leave = useMutation(api.presence.leave);
  const online = useQuery(api.presence.onlineUsers, { page }) ?? [];
  const { convexUserId } = useContext(BaseContext);


  useEffect(() => {
    if (!convexUserId) return;
    const send = () => heartbeat({ deviceId, page, userId: convexUserId }).catch(() => {});
    send(); // initial ping

    const interval = setInterval(send, 20_000); // 20s heartbeat; server timeout 60s
    const onVis = () => {
      if (document.visibilityState === "visible") send();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVis);
      // best-effort leave; presence also expires naturally
      leave({ deviceId, page, userId: convexUserId }).catch(() => {});
    };
  }, [heartbeat, leave, deviceId, page, convexUserId]);

  const onlineMap = new Map(online.map((u) => [u.userId, u.lastSeen]));
  const isOnline = (userId: string, timeoutMs = 60_000) =>
    (onlineMap.get(userId) ?? 0) >= Date.now() - timeoutMs;

  return { online, isOnline, deviceId };
}
